import Route from '@ember/routing/route';
import { service } from '@ember/service';
import { setUser, setGlobalContextProperty } from 'tio-common/utils/datadog';
import type SessionContextService from 'tio-employee/services/session-context';
import type ZendeskService from 'tio-employee/services/zendesk';
import type RouterService from '@ember/routing/router-service';
import type PartnerService from 'tio-employee/services/partner';
import type SessionService from 'tio-employee/services/session';
import type FeaturesService from 'tio-common/services/features';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type GoogleAnalyticsService from 'tio-employee/services/google-analytics';
import type CableService from 'tio-common/services/cable';
import type TelemetryService from 'tio-common/services/telemetry';
import type ThoughtBubbleRuntimeService from 'tio-common/services/thought-bubble-runtime';
import type ThoughtBubbleBuilderService from 'tio-common/services/thought-bubble-builder';
import type Transition from '@ember/routing/transition';
import ENV from 'tio-employee/config/environment';
import type UserModel from 'tio-data/models/user';
import {
  wellnessInfoRequest,
  enqueueWellnessRecalculationRequest,
  getZendeskJwtRequest,
} from 'tio-common/requests/user';

function setupForethought(
  firstName: string,
  lastName: string,
  fullName: string,
  jwt: string,
  tioUserJwt: string | undefined,
  tioApiKey: string,
  email = '',
  partnerName = '',
  onReady?: () => void
) {
  document.querySelector('#forethought-chat')?.remove();
  document.querySelector('#forethought-widget-embed-script')?.remove();
  const script = document.createElement('script');
  script.setAttribute('src', 'https://solve-widget.forethought.ai/embed.js');
  script.setAttribute('id', 'forethought-widget-embed-script');
  script.setAttribute('data-dd-privacy', 'hidden');
  script.setAttribute('data-api-key', '21bf8592-1b59-4758-84ef-f82c33c6462e');

  script.setAttribute('data-ft-First-Name', firstName);
  script.setAttribute('data-ft-Last-Name', lastName);
  script.setAttribute('data-ft-Full-Name', fullName);
  script.setAttribute('data-ft-zendesk-jwt', jwt);
  if (tioUserJwt) {
    script.setAttribute('data-ft-tio-user-jwt', tioUserJwt);
  }
  script.setAttribute('data-ft-tio-api-key', tioApiKey);
  script.setAttribute('data-ft-Email', email);
  script.setAttribute('data-ft-Partner-Name', partnerName);
  script.setAttribute('data-ft-workflow-tag', ENV.tioEnvironment);
  script.setAttribute('config-ft-persistence-id', ENV.tioEnvironment);
  script.setAttribute('position-x', 'left');

  if (onReady) {
    script.addEventListener('load', () => {
      // Poll for Forethought global to be available (widget initializes async after script load)
      const maxWaitMs = 5000;
      const pollIntervalMs = 100;
      const startTime = Date.now();

      const checkForethought = () => {
        if (window.Forethought) {
          onReady();
          return;
        }
        if (Date.now() - startTime >= maxWaitMs) {
          onReady();
          return;
        }
        setTimeout(checkForethought, pollIntervalMs);
      };

      checkForethought();
    });
  }

  document.head.append(script);
}

export default class AuthenticatedRoute extends Route {
  @service declare session: SessionService;
  @service declare sessionContext: SessionContextService;
  @service declare features: FeaturesService;
  @service declare zendesk: ZendeskService;
  @service declare router: RouterService;
  @service declare partner: PartnerService;
  @service declare store: StoreService;
  @service declare googleAnalytics: GoogleAnalyticsService;
  @service declare cable: CableService;
  @service declare telemetry: TelemetryService;
  @service declare thoughtBubbleRuntime: ThoughtBubbleRuntimeService;
  @service declare thoughtBubbleBuilder: ThoughtBubbleBuilderService;

  async beforeModel(transition: Transition) {
    const loggedIn = this.session.requireAuthentication(transition, 'login');

    if (!loggedIn) {
      return;
    }

    // Configure WebSocket connection for ActionCable
    const token = this.session.data.authenticated?.access_token;
    if (token && !this.cable.isConfigured) {
      this.cable.configure(ENV.apiHost, ENV.apiKey, token, ENV.wsapiHost);
    }

    if (!this.sessionContext.isLoaded) {
      try {
        await this.sessionContext.load();
      } catch (e) {
        console.error('[route:authenticated] Could not load session context', e);
        this.sessionContext.logout();
        return;
      }
    }

    if (!this.sessionContext.user?.id) {
      console.error(
        '[route:authenticated] Missing user id in session context; redirecting to login'
      );
      this.sessionContext.logout();
      return;
    }

    try {
      this.zendesk.hideAllChatWidgets();
      this.zendesk.setWidgetLocale();
    } catch (e) {
      console.error('[route:authenticated] Could not set zendesk user', e);
    }

    try {
      if (this.partner.isZendeskEnabled) {
        const zendeskJwtResp = await this.store.request(getZendeskJwtRequest());
        const zendeskJwt = zendeskJwtResp.content as { token: string };

        setupForethought(
          this.sessionContext.user.person?.firstName,
          this.sessionContext.user.person?.lastName,
          this.sessionContext.user.person?.fullName,
          zendeskJwt.token,
          token,
          ENV.apiKey,
          this.sessionContext.user.employees?.[0]?.email,
          this.sessionContext.user.employees?.[0]?.company.name,
          () => this.zendesk.hideForethoughtWidget()
        );
        window.addEventListener('message', (e) => {
          if (e.data.event === 'forethoughtWidgetIntegrationHandoff') {
            this.zendesk.zendeskAction('messenger', 'resetWidget');
          }
          // Re-hide widget when user closes chat so only sidebar Support can reopen it
          if (e.data.event === 'forethoughtWidgetClosed') {
            this.zendesk.hideForethoughtWidget();
          }
        });
      }
    } catch (e) {
      // This is a non-blocking error. If Forethought fails to load, it shouldn't prevent the rest of the app from loading.
      console.error('[route:authenticated] Could not setup Forethought chat', e);
    }

    try {
      this.setupDatadogIdentity();
    } catch (e) {
      console.error('Error setting up DataDog user', e);
    }

    try {
      this.setupDatadogPrivateContext();
    } catch (e) {
      console.error('Error setting up DataDog private context', e);
    }

    // Mirror user identity to custom telemetry
    try {
      const currentRole = this.sessionContext.currentRole;
      this.telemetry.setUser({
        id: this.sessionContext.user.id,
        personId: this.sessionContext.user.person?.id ?? undefined,
        name: this.sessionContext.user.person?.fullName,
        role: currentRole?.role,
        relationshipType: currentRole?.relationshipType,
      });

      this.telemetry.setContext({
        company_id: this.sessionContext.company?.id ?? undefined,
      });
    } catch (e) {
      console.error('Error setting up telemetry user', e);
    }

    // Initialize thought bubble runtime for auto-triggering flows on route change
    // Outside env gate — runtime is safe on all environments (only triggers flows that exist in the API)
    try {
      this.thoughtBubbleRuntime.initialize({
        company: this.sessionContext.user?.employees?.[0]?.company,
        employee: this.sessionContext.user?.employees?.[0],
        roles: this.sessionContext.currentRole ? [this.sessionContext.currentRole] : [],
      });
    } catch (e) {
      console.error('Error initializing thought bubble runtime', e);
    }

    // Check if this page was opened as a visual builder target
    // Outside env gate because it's self-gating via ?tb-builder= query param
    try {
      this.thoughtBubbleBuilder.checkActivation();
    } catch (e) {
      console.error('Error checking thought bubble builder activation', e);
    }

    // @ts-expect-error: transition.intent may have url (URL transition) or name (named route transition)
    const isRootUrlTransition = transition.intent?.url === '/';
    const targetRouteName = transition.to?.name;
    const isRootNamedTransition =
      targetRouteName === 'authenticated.index' || targetRouteName === 'authenticated';

    if (isRootUrlTransition || isRootNamedTransition) {
      if (this.sessionContext.currentRole?.relationshipType === 'PARTICIPANT') {
        this.router.transitionTo('authenticated.dashboard');
      } else {
        this.router.transitionTo('authenticated.admin');
      }
    }
  }

  async model() {
    const userId = this.sessionContext.user?.id;
    if (!userId) {
      console.error('[route:authenticated] Missing user id in model hook; redirecting to login');
      this.router.transitionTo('login');
      return {
        user: this.sessionContext.user as unknown as UserModel,
        wellnessData: null,
      };
    }
    const user = await this.store.findRecord<UserModel>('user', userId);

    let wellnessData: Record<string, unknown> | null = null;
    try {
      const response = await this.store.request(wellnessInfoRequest(userId));
      wellnessData = response.content as Record<string, unknown>;
    } catch (e) {
      console.error('[route:authenticated] Failed to load wellness train:', e);
    }

    // If no wellness data exists, trigger a background recalculation (fire-and-forget).
    // Data will be available on the next page load.
    if (!wellnessData) {
      this.store.request(enqueueWellnessRecalculationRequest(userId)).catch((e) => {
        console.error(
          '[route:authenticated] Failed to enqueue background wellness recalculation:',
          e
        );
      });
    }

    return { user, wellnessData };
  }

  afterModel() {
    // Initialize Google Analytics tracking now that user is fully authenticated
    // and currentRole is available
    this.googleAnalytics.initializeTracking();

    // Track this route transition with user context
    const currentRole = this.sessionContext.currentRole;
    this.googleAnalytics.trackRouteTransition('authenticated', {
      user_authenticated: true,
      role_type: currentRole?.relationshipType,
      company_id: currentRole?.company?.id,
    });

    // Note: Wellness train data is now loaded in model() hook
  }

  setupDatadogIdentity() {
    const { user } = this.sessionContext;
    const currentRole = this.sessionContext.currentRole;

    setUser({
      // @ts-expect-error: not sure
      id: user.id,
      name: user.person?.fullName,
      role: currentRole?.role,
      relationshipType: currentRole?.relationshipType,
    });
  }

  setupDatadogPrivateContext() {
    const { user } = this.sessionContext;
    const currentRole = this.sessionContext.currentRole;

    if (user?.id) {
      setGlobalContextProperty('tio.user.id', user.id);
    }
    if (user?.person?.fullName) {
      setGlobalContextProperty('tio.user.name', user.person.fullName);
    }
    if (currentRole?.company?.id) {
      setGlobalContextProperty('tio.partner.id', currentRole.company.id);
    }
    if (currentRole?.company?.name) {
      setGlobalContextProperty('tio.partner.name', currentRole.company.name);
    }
  }
}
